import React, { useState, useEffect, useRef } from 'react';
import '../styles/dashboard.css';
import '../styles/counselors.css';
import '../styles/leads.css';
import '../styles/visa.css';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import PrintReportButton from '../components/PrintReportButton';
import DateRangeFilter from '../components/DateRangeFilter';
import ViewRecordModal from '../components/ViewRecordModal';
import EditVisaEnquiry from './EditVisaEnquiry';
import ListPagination from '../components/ListPagination';
import useListPagination from '../hooks/useListPagination';
import { EMPTY_DATE_RANGE, hasDateRange, isDateInRange } from '../utils/dateRange';
import { applicantDisplayName } from '../constants/visaEnquiryDefaults';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPassport, faEdit, faTrash, faSearch, faEye, faChevronDown, faTimes,
} from '@fortawesome/free-solid-svg-icons';

const API_URL = process.env.REACT_APP_API_URL || 'https://unilinkapi-production.up.railway.app';

export default function VisaEnquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [viewRecordId, setViewRecordId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState(EMPTY_DATE_RANGE);
  const [activeFilter, setActiveFilter] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [columnFilters, setColumnFilters] = useState({
    applicantName: [], intendedCountry: [], purposeOfVisit: [],
  });
  const [filterSearch, setFilterSearch] = useState({
    applicantName: '', intendedCountry: '', purposeOfVisit: '',
  });

  useEffect(() => { fetchEnquiries(); }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.filter-dropdown-container')) setActiveFilter(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchEnquiries = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/visa-enquiries`);
      const data = await res.json();
      if (data.success) setEnquiries(data.enquiries || []);
      else setError(data.message || 'Failed to fetch visa enquiries');
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete visa enquiry for ${name}?`)) return;
    try {
      const res = await fetch(`${API_URL}/visa-enquiries/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) fetchEnquiries();
      else alert(data.message || 'Failed to delete');
    } catch (err) {
      alert('Network error. Please try again.');
    }
  };

  const getUniqueValues = (column) => {
    const values = new Set();
    enquiries.forEach((row) => {
      let value;
      switch (column) {
        case 'applicantName': value = applicantDisplayName(row); break;
        case 'intendedCountry': value = row.intended_country; break;
        case 'purposeOfVisit': value = row.purpose_of_visit; break;
        default: value = '';
      }
      if (value) values.add(value);
    });
    return Array.from(values).sort();
  };

  const getFilteredOptions = (column) => {
    const unique = getUniqueValues(column);
    const term = filterSearch[column].toLowerCase();
    return term ? unique.filter((v) => v.toLowerCase().includes(term)) : unique;
  };

  const toggleFilterValue = (column, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [column]: prev[column].includes(value)
        ? prev[column].filter((v) => v !== value)
        : [...prev[column], value],
    }));
  };

  const clearColumnFilter = (column) => {
    setColumnFilters((prev) => ({ ...prev, [column]: [] }));
    setFilterSearch((prev) => ({ ...prev, [column]: '' }));
  };

  const clearAllFilters = () => {
    setColumnFilters({ applicantName: [], intendedCountry: [], purposeOfVisit: [] });
    setFilterSearch({ applicantName: '', intendedCountry: '', purposeOfVisit: '' });
    setSearchTerm('');
    setDateRange(EMPTY_DATE_RANGE);
  };

  const filtered = enquiries.filter((row) => {
    const name = applicantDisplayName(row);
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      if (
        !name.toLowerCase().includes(term) &&
        !(row.email || '').toLowerCase().includes(term) &&
        !(row.enquiry_id || '').toLowerCase().includes(term) &&
        !(row.intended_country || '').toLowerCase().includes(term)
      ) return false;
    }
    if (columnFilters.applicantName.length > 0 && !columnFilters.applicantName.includes(name)) return false;
    if (columnFilters.intendedCountry.length > 0 && !columnFilters.intendedCountry.includes(row.intended_country)) return false;
    if (columnFilters.purposeOfVisit.length > 0 && !columnFilters.purposeOfVisit.includes(row.purpose_of_visit)) return false;
    if (hasDateRange(dateRange) && !isDateInRange(row.created_at, dateRange.from, dateRange.to)) return false;
    return true;
  });

  const hasActiveFilters =
    Object.values(columnFilters).some((f) => f.length > 0) ||
    searchTerm.trim() ||
    hasDateRange(dateRange);

  const { paginatedItems, totalPages, currentPage, goToPage } = useListPagination(
    filtered,
    [searchTerm, JSON.stringify(columnFilters), dateRange.from, dateRange.to].join('|')
  );

  const FilterDropdown = ({ column, label }) => {
    const triggerRef = useRef(null);
    const isActive = activeFilter === column;
    const hasFilters = columnFilters[column].length > 0;
    const filteredOptions = getFilteredOptions(column);
    const allOptions = getUniqueValues(column);

    const handleToggle = () => {
      if (isActive) { setActiveFilter(null); return; }
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = 380;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      let top = (spaceBelow >= dropdownHeight || spaceBelow > spaceAbove)
        ? rect.bottom + 8
        : rect.top - dropdownHeight - 8;
      let left = rect.left;
      if (left + 280 > window.innerWidth) left = window.innerWidth - 300;
      setDropdownPosition({ top, left });
      setActiveFilter(column);
    };

    return (
      <div className="filter-dropdown-container">
        <button
          ref={triggerRef}
          type="button"
          className={`filter-trigger ${hasFilters ? 'has-filters' : ''}`}
          onClick={handleToggle}
        >
          {label}
          <FontAwesomeIcon icon={faChevronDown} className="filter-icon" />
          {hasFilters && <span className="filter-count">{columnFilters[column].length}</span>}
        </button>
        {isActive && (
          <div className="filter-dropdown" style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}>
            <div className="filter-dropdown-header">
              <input
                type="text"
                className="filter-search-input"
                placeholder="Search..."
                value={filterSearch[column]}
                onChange={(e) => setFilterSearch((prev) => ({ ...prev, [column]: e.target.value }))}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="filter-options">
              <label className="filter-option select-all">
                <input
                  type="checkbox"
                  checked={columnFilters[column].length === allOptions.length && allOptions.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) setColumnFilters((prev) => ({ ...prev, [column]: allOptions }));
                    else clearColumnFilter(column);
                  }}
                />
                <span className="filter-label">Select All</span>
              </label>
              {filteredOptions.length > 0 ? filteredOptions.map((value, i) => (
                <label key={i} className="filter-option">
                  <input
                    type="checkbox"
                    checked={columnFilters[column].includes(value)}
                    onChange={() => toggleFilterValue(column, value)}
                  />
                  <span className="filter-label">{value}</span>
                </label>
              )) : <div className="no-results">No results found</div>}
            </div>
            <div className="filter-dropdown-footer">
              <button type="button" className="btn-clear-filter" onClick={() => clearColumnFilter(column)}>Clear</button>
              <button type="button" className="btn-apply-filter" onClick={() => setActiveFilter(null)}>Apply</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const printColumns = [
    { header: 'Enquiry ID', value: (r) => r.enquiry_id },
    { header: 'Applicant Name', value: (r) => applicantDisplayName(r) },
    { header: 'Email', value: (r) => r.email || '-' },
    { header: 'Mobile', value: (r) => r.mobile_no || '-' },
    { header: 'Intended Country', value: (r) => r.intended_country || '-' },
    { header: 'Purpose', value: (r) => r.purpose_of_visit || '-' },
    { header: 'Travel Month', value: (r) => r.travel_month_year || '-' },
    {
      header: 'Submitted',
      value: (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'),
    },
  ];

  const formatDate = (val) => {
    if (!val) return '-';
    return new Date(val).toLocaleDateString();
  };

  return (
    <>
      <Sidebar />
      <div className="main-content" id="mainContent">
        <Header />
        <div className="counselors-container">
          <div className="counselors-header">
            <div className="header-left">
              <h1>
                <FontAwesomeIcon icon={faPassport} className="page-icon" />
                Visa Enquiries
              </h1>
            </div>
            <div className="header-right">
              <PrintReportButton
                show={hasActiveFilters}
                title="Visa Enquiries Report"
                columns={printColumns}
                rows={filtered}
              />
              {hasActiveFilters && (
                <button type="button" className="btn-clear-all-filters" onClick={clearAllFilters}>
                  <FontAwesomeIcon icon={faTimes} /> Clear All Filters
                </button>
              )}
            </div>
          </div>

          {error && (
            <div style={{ padding: '16px', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '16px', border: '1px solid #fecaca' }}>
              {error}
            </div>
          )}

          <div className="page-toolbar">
            <div className="page-search">
              <FontAwesomeIcon icon={faSearch} className="page-search-icon" />
              <input
                type="text"
                className="page-search-input"
                placeholder="Search by name, email, enquiry ID, country..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button type="button" className="page-search-clear" onClick={() => setSearchTerm('')}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
            <DateRangeFilter
              label="Submitted Date"
              value={dateRange}
              onChange={setDateRange}
            />
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}><p>Loading visa enquiries...</p></div>
          ) : (
            <>
              <div className="counselors-table-container">
                <table className="counselors-table">
                  <thead>
                    <tr>
                      <th>Enquiry ID</th>
                      <th><FilterDropdown column="applicantName" label="Applicant Name" /></th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th><FilterDropdown column="intendedCountry" label="Intended Country" /></th>
                      <th><FilterDropdown column="purposeOfVisit" label="Purpose of Visit" /></th>
                      <th>Submitted</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedItems.length > 0 ? paginatedItems.map((row) => (
                      <tr key={row.id}>
                        <td><span className="counselor-id">{row.enquiry_id}</span></td>
                        <td>
                          <Link to={`/edit-visa-enquiry/${row.id}`} className="table-name-link">
                            {applicantDisplayName(row)}
                          </Link>
                        </td>
                        <td>{row.email || '—'}</td>
                        <td>{row.mobile_no || '—'}</td>
                        <td>{row.intended_country || '—'}</td>
                        <td>{row.purpose_of_visit || '—'}</td>
                        <td>{formatDate(row.created_at)}</td>
                        <td>
                          <div className="action-buttons">
                            <button type="button" className="btn-icon btn-view" title="View" onClick={() => setViewRecordId(row.id)}>
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                            <Link to={`/edit-visa-enquiry/${row.id}`} className="btn-icon btn-edit" title="Edit">
                              <FontAwesomeIcon icon={faEdit} />
                            </Link>
                            <button type="button" className="btn-icon btn-delete" title="Delete" onClick={() => handleDelete(row.id, applicantDisplayName(row))}>
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                          {hasActiveFilters ? 'No visa enquiries match the selected filters' : 'No visa enquiries found'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <ListPagination
                total={filtered.length}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
                itemLabel="enquiry"
              />
            </>
          )}
        </div>
      </div>
      {viewRecordId && (
        <ViewRecordModal title="View Visa Enquiry" onClose={() => setViewRecordId(null)}>
          <EditVisaEnquiry recordId={viewRecordId} viewOnly />
        </ViewRecordModal>
      )}
    </>
  );
}
